/**
 * HTML graphics templates and the export pipeline contracts.
 *
 * A template is a versioned, self-contained HTML bundle (fonts and images
 * inlined) that exposes a deterministic entry point:
 *
 *   window.prism.renderAt(timeSeconds, data, info)
 *
 * It must draw purely from its arguments. The host freezes the clock and the
 * random number generator, blocks all network access, and renders on a
 * transparent background, so preview and export produce the same pixels.
 */
import { z } from 'zod';
import { ObjectRefSchema, LocationSpecsSchema, ProbeResultSchema, CONTRACT_VERSION } from './jobs.ts';
import { GraphicValueSchema, SequenceDocumentSchema, SubtitleBottomSchema, allAudioTracks, allClips } from './sequence.ts';
import { appliedCrossfade } from './audio-levels.ts';
import type { GraphicEngine, GraphicValue, OgrafManifest } from './ograf.ts';
import { frameRateLabel, type Rational } from './time.ts';
import { aspectLabel, sameAspect } from './framing.ts';
import { interlacedName } from './sequence-formats.ts';
import { ResolvedCueStyleSchema, type SidecarFormat } from './subtitles.ts';
import { compareVersions } from './semver.ts';
import { isDefaultCueStyle } from './caption.ts';

export const TemplateFieldSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/),
  label: z.string(),
  type: z.enum(['text', 'color']),
  default: z.string(),
  maxLength: z.number().int().positive().optional(),
  required: z.boolean().default(false),
});
export type TemplateField = z.infer<typeof TemplateFieldSchema>;

export const TemplateManifestSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]*$/),
  version: z.number().int().positive(),
  name: z.string(),
  description: z.string(),
  kind: z.enum(['lower_third', 'title_card', 'logo', 'overlay']),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** Title-safe rectangle the design stays inside, in template pixels. */
  safeArea: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }),
  defaultDurationSeconds: z.number().positive(),
  animation: z.object({ inSeconds: z.number().nonnegative(), outSeconds: z.number().nonnegative() }),
  fields: z.array(TemplateFieldSchema),
  fonts: z.array(z.object({ family: z.string(), file: z.string(), license: z.string() })),
  entry: z.string(),
  previewTimeSeconds: z.number().nonnegative(),
});
export type TemplateManifest = z.infer<typeof TemplateManifestSchema>;

export interface GraphicTemplateDTO {
  id: string;
  version: number;
  /** The manifest; for a graphics asset, its field defaults are the ones set on the asset page. */
  manifest: TemplateManifest;
  checksumSha256: string;
  previewUrl: string | null;
  /** A graphics asset's template (null: built in). */
  asset?: { id: string; title: string; assetCode: string; thumbnailUrl: string | null } | null;
  /** The newest version of its template (the graphics picker offers only these; older ones stay for the sequences that use them). */
  latest?: boolean;
  /** False for a graphics asset in the recycle bin or purged: kept for the sequences that use it, not offered for new ones. */
  enabled?: boolean;
  /** 'renderat' (an HTML template) or 'ograf-v1' (0.93.0). For OGraf, `manifest` is PRISM's summary of `ograf`. */
  engine?: GraphicEngine;
  /** The OGraf manifest as it is in the package (OGraf only). */
  ograf?: OgrafManifest | null;
  /** The asset version behind it is ready (its ingest finished); a newer-version marker only points at ready versions. */
  ready?: boolean;
}

/** Render one still of a template (a graphics asset's poster) at a moment, with given field values. */
export const RenderPosterJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  templateRef: ObjectRefSchema,
  templateChecksumSha256: z.string(),
  manifest: TemplateManifestSchema,
  data: z.record(z.string(), z.string()),
  timeSeconds: z.number().nonnegative(),
  output: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
  destination: ObjectRefSchema,
  locations: LocationSpecsSchema,
});
export type RenderPosterJob = z.infer<typeof RenderPosterJobSchema>;

/* ---------------------------------------------------------------- renderer jobs */

/** How a rendered graphic is encoded: PNG in MOV, lossless, for exports to compose; ProRes 4444 with alpha for NLEs. */
export const GRAPHIC_CODECS = ['png', 'prores4444'] as const;
export type GraphicCodec = (typeof GRAPHIC_CODECS)[number];

export const RenderGraphicJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  /** Self-contained HTML bundle; the renderer never fetches anything. */
  templateRef: ObjectRefSchema,
  templateChecksumSha256: z.string(),
  manifest: TemplateManifestSchema,
  data: z.record(z.string(), z.string()),
  frameRate: z.object({ num: z.number().int().positive(), den: z.number().int().positive() }),
  durationTicks: z.number().int().positive(),
  output: z.object({ width: z.number().int(), height: z.number().int() }),
  destination: ObjectRefSchema,
  locations: LocationSpecsSchema,
  /** The file's video codec: PNG in MOV (exports), or ProRes 4444 with alpha (Send to edit, 1.0.0-rc.6). */
  codec: z.enum(GRAPHIC_CODECS).optional(),
});
export type RenderGraphicJob = z.infer<typeof RenderGraphicJobSchema>;

/* ---------------------------------------------------------------- OGraf jobs (0.93.0) */

/**
 * An unpacked OGraf package in storage: every file at `<prefix><path>`, each with its SHA-256, so the renderer
 * reads exactly the bytes ingest stored. `checksumSha256` is the original zip's.
 */
export const OgrafPackageRefSchema = z.object({
  locationId: z.string(),
  prefix: z.string(),
  checksumSha256: z.string(),
  main: z.string(),
  files: z.array(z.object({ path: z.string(), mime: z.string(), size: z.number().int().nonnegative(), sha256: z.string() })).max(500),
});
export type OgrafPackageRef = z.infer<typeof OgrafPackageRefSchema>;

const OgrafManifestSchema = z.looseObject({ name: z.string(), main: z.string() });

export const RenderOgrafJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  engine: z.literal('ograf-v1'),
  package: OgrafPackageRefSchema,
  manifest: OgrafManifestSchema,
  /** The graphic's authored picture size (it lays out at this size; the output is scaled from it). */
  size: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
  data: z.record(z.string(), GraphicValueSchema),
  stepMarks: z.array(z.number().int().nonnegative()).optional(),
  updates: z.array(z.object({ atTicks: z.number().int().nonnegative(), data: z.record(z.string(), GraphicValueSchema) })).optional(),
  /** The sequence's rate: the schedule and every frame's timestamp are computed at it. */
  frameRate: z.object({ num: z.number().int().positive(), den: z.number().int().positive() }),
  durationTicks: z.number().int().positive(),
  output: z.object({ width: z.number().int(), height: z.number().int() }),
  destination: ObjectRefSchema,
  locations: LocationSpecsSchema,
  codec: z.enum(GRAPHIC_CODECS).optional(),
});
export type RenderOgrafJob = Omit<z.infer<typeof RenderOgrafJobSchema>, 'manifest'> & { manifest: OgrafManifest };

/** An OGraf graphic's poster: one still at `timestampMs` of a default-length clip with the default data. */
export const RenderOgrafPosterJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  engine: z.literal('ograf-v1'),
  package: OgrafPackageRefSchema,
  manifest: OgrafManifestSchema,
  size: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
  data: z.record(z.string(), GraphicValueSchema),
  frameRate: z.object({ num: z.number().int().positive(), den: z.number().int().positive() }),
  durationTicks: z.number().int().positive(),
  timestampMs: z.number().nonnegative(),
  output: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
  destination: ObjectRefSchema,
  locations: LocationSpecsSchema,
});
export type RenderOgrafPosterJob = Omit<z.infer<typeof RenderOgrafPosterJobSchema>, 'manifest'> & { manifest: OgrafManifest };

/**
 * One cue shown in a burn-in interval (1.9.0): its text with its markup (<i>, <b>, <u>; the renderer reads it with
 * caption.ts cueRuns), its speaker and its effective style (effectiveCueStyle, resolved by the API's export context).
 */
export const RenderCueShownSchema = z.object({
  text: z.string().max(2000),
  speaker: z.string().max(120).optional(),
  style: ResolvedCueStyleSchema,
});
export type RenderCueShown = z.infer<typeof RenderCueShownSchema>;

/**
 * One burn-in picture (one full-frame PNG, `<destinationPrefix><id>.png`). 1.9.0: one entry per caption.ts
 * cueIntervals interval with a distinct key (`id` is the interval's key; a track without overlaps gives one entry per
 * cue, named by the cue's id as before), and `active` lists every cue shown in it, in its place and style; the
 * renderer lays each out in Node with cueLayout and cueRuns.
 *
 * `text` and `speaker` are the first shown cue's, for a renderer older than CUE_STYLE_MIN_RENDERER (it strips the
 * unknown `active` and draws that one, bottom-centred and plain); the workflow then fails the export when the job
 * needed styles (cuesNeedStyledRenderer), from the version the renderer reports. Left out `active` (a job from a
 * 1.8.3 workflow): the renderer draws `text` and `speaker` with DEFAULT_TRACK_STYLE.
 */
export const RenderCueEntrySchema = z.object({
  id: z.string().min(1).max(400),
  text: z.string(),
  speaker: z.string().optional(),
  active: z.array(RenderCueShownSchema).min(1).max(16).optional(),
});
export type RenderCueEntry = z.infer<typeof RenderCueEntrySchema>;

/** The first graphics renderer that draws `active` (styles, cells, inline italics, cues shown at once). */
export const CUE_STYLE_MIN_RENDERER = '1.9.0';

/** Whether a renderer's reported version (RenderResult.renderer.version, or renderCues' `renderer`) draws styled cues. */
export function rendererDrawsCueStyles(version: string | null | undefined): boolean {
  if (!version) return false;
  try { return compareVersions(version.replace(/^v/, ''), CUE_STYLE_MIN_RENDERER) >= 0; } catch { return false; }
}

const INLINE_MARKUP = /<\/?[ibu](?:[.\s][^>]*)?>/i;
/**
 * Whether drawing these entries needs a 1.9.0 renderer: more than one cue at once, a style other than the default,
 * or inline <i>, <b>, <u>. A job that does not can go to an older renderer as it is.
 */
export function cuesNeedStyledRenderer(entries: readonly RenderCueEntry[]): boolean {
  return entries.some((e) => (e.active ?? []).length > 1 || (e.active ?? []).some((a) =>
    !isDefaultCueStyle(a.style) || INLINE_MARKUP.test(a.text)));
}

export const RenderCuesJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  cues: z.array(RenderCueEntrySchema),
  language: z.string(),
  output: z.object({ width: z.number().int(), height: z.number().int() }),
  /** 1.7.0: the format's subtitle height (a fraction of the frame, captionLayout); left out: the orientation's default. */
  subtitleBottom: SubtitleBottomSchema.optional(),
  /** Each cue becomes <prefix><cueId>.png */
  destinationPrefix: ObjectRefSchema,
  locations: LocationSpecsSchema,
});
export type RenderCuesJob = z.infer<typeof RenderCuesJobSchema>;

export interface RenderResult {
  destination: { locationId: string; key: string };
  frames: number;
  sizeBytes: number;
  checksumSha256: string;
  renderer: { engine: string; version: string };
}

/* ---------------------------------------------------------------- export composition */

export interface ExportPresetInfo {
  label: string;
  /** web: H.264 MP4 that plays in browsers; broadcast: mastering and delivery formats. */
  group: 'web' | 'broadcast';
  description: string;
  extension: 'mp4' | 'mxf' | 'mov';
  mime: string;
  /**
   * Web presets (1.0.0-rc.11): the largest short side, keeping the sequence's shape (1080×1920 stays 1080×1920 in
   * 1080p, UHD becomes 1920×1080); smaller sequences keep their size.
   */
  maxShortSide?: number;
  crf?: number;
  audioKbps?: number;
  /** Broadcast presets: the exact frame (the sequence is scaled and letterboxed into it). */
  frame?: { width: number; height: number };
  /** Interlaced, top field first: for 25 and 50 fps sequences (1080i50) and 29.97 and 59.94 fps ones (1080i59.94). */
  interlaced?: boolean;
}

export const EXPORT_PRESET_IDS = ['h264-1080p', 'h264-720p', 'avci100-1080i', 'xdcam-hd422-1080i', 'prores-hq-1080i', 'prores-hq-1080p', 'dnxhr-hq-1080p'] as const;
export type ExportPreset = (typeof EXPORT_PRESET_IDS)[number];
/**
 * What an asset export can be (0.99.0): one of the presets, or `original`, the original's own format with no
 * transcode (the whole file copied byte for byte, or a range rewrapped by stream copy). Sequences only render presets.
 */
export const EXPORT_FORMAT_IDS = [...EXPORT_PRESET_IDS, 'original'] as const;
export type ExportFormatId = (typeof EXPORT_FORMAT_IDS)[number];

const HD = { width: 1920, height: 1080 };
export const EXPORT_PRESETS: Record<ExportPreset, ExportPresetInfo> = {
  'h264-1080p': { label: 'H.264 1080p (delivery)', group: 'web', description: 'MP4, H.264 High, AAC 256 kb/s', extension: 'mp4', mime: 'video/mp4', maxShortSide: 1080, crf: 18, audioKbps: 256 },
  'h264-720p': { label: 'H.264 720p (review)', group: 'web', description: 'MP4, H.264 High, AAC 160 kb/s', extension: 'mp4', mime: 'video/mp4', maxShortSide: 720, crf: 22, audioKbps: 160 },
  'avci100-1080i': { label: 'AVC-Intra 100 · 1080i · MXF', group: 'broadcast', description: 'MXF OP1a, AVC-Intra Class 100 (10-bit 4:2:2), 1920×1080 interlaced TFF, 2 × PCM 24-bit mono', extension: 'mxf', mime: 'application/mxf', frame: HD, interlaced: true },
  'xdcam-hd422-1080i': { label: 'XDCAM HD422 50 Mb/s · 1080i · MXF', group: 'broadcast', description: 'MXF OP1a, MPEG-2 4:2:2 50 Mb/s long GOP, 1920×1080 interlaced TFF, 2 × PCM 24-bit mono', extension: 'mxf', mime: 'application/mxf', frame: HD, interlaced: true },
  'prores-hq-1080i': { label: 'ProRes 422 HQ · 1080i · MOV', group: 'broadcast', description: 'QuickTime, Apple ProRes 422 HQ (10-bit), 1920×1080 interlaced TFF, PCM 24-bit stereo', extension: 'mov', mime: 'video/quicktime', frame: HD, interlaced: true },
  'prores-hq-1080p': { label: 'ProRes 422 HQ · 1080p · MOV', group: 'broadcast', description: 'QuickTime, Apple ProRes 422 HQ (10-bit), 1920×1080 progressive, PCM 24-bit stereo', extension: 'mov', mime: 'video/quicktime', frame: HD },
  'dnxhr-hq-1080p': { label: 'DNxHR HQ · 1080p · MXF', group: 'broadcast', description: 'MXF OP1a, Avid DNxHR HQ (8-bit 4:2:2), 1920×1080 progressive, 2 × PCM 24-bit mono', extension: 'mxf', mime: 'application/mxf', frame: HD },
};

/**
 * Why a preset cannot render a sequence at this frame rate; null when it can. Interlaced presets (1.0.0-rc.11) take
 * 25 and 50 fps sequences (1080i50) and 29.97 and 59.94 fps ones (1080i59.94).
 */
export function presetProblem(preset: ExportPreset, frameRate: { num: number; den: number }): string | null {
  const p = EXPORT_PRESETS[preset];
  const fps = frameRate.num / frameRate.den;
  if (p.interlaced && !interlacedName(frameRate)) {
    return `${p.label} is interlaced: it needs a 25 or 50 fps sequence (1080i50) or a 29.97 or 59.94 fps one (1080i59.94), not ${Number(fps.toFixed(3))} fps`;
  }
  return null;
}

/**
 * The rate an interlaced export renders at (1.0.0-rc.11): its field rate, twice the frame rate of 1080i. A 25 or
 * 29.97 fps sequence renders at 50 or 59.94 (each frame twice for progressive sources, each field on its own for
 * interlaced ones); a 50 or 59.94 fps sequence at its own rate, each frame becoming one field.
 */
export function fieldRate(frameRate: Rational): Rational {
  const fps = frameRate.num / frameRate.den;
  return fps > 31 ? { num: frameRate.num, den: frameRate.den } : { num: frameRate.num * 2, den: frameRate.den };
}

/** The rate an export renders its picture at: the field rate for interlaced presets, else the sequence's rate. */
export function compositeRate(preset: ExportPreset, frameRate: Rational): Rational {
  return EXPORT_PRESETS[preset].interlaced ? fieldRate(frameRate) : { num: frameRate.num, den: frameRate.den };
}

/** The frame rate of the exported file: half the field rate for interlaced presets, else the sequence's rate. */
export function outputFrameRate(preset: ExportPreset, frameRate: Rational): Rational {
  if (!EXPORT_PRESETS[preset].interlaced) return { num: frameRate.num, den: frameRate.den };
  const f = fieldRate(frameRate);
  return f.num % 2 === 0 ? { num: f.num / 2, den: f.den } : { num: f.num, den: f.den * 2 };
}

/**
 * The exported file's frame (1.0.0-rc.11): broadcast presets have a fixed frame; web presets keep the sequence's shape
 * with its short side at most maxShortSide (even sizes).
 */
export function exportOutputSize(preset: ExportPreset, sequence: { width: number; height: number }): { width: number; height: number } {
  const p = EXPORT_PRESETS[preset];
  if (p.frame) return { ...p.frame };
  const max = p.maxShortSide ?? 1080;
  const { width, height } = sequence;
  const short = Math.min(width, height);
  if (short <= max) return { width: width - (width % 2), height: height - (height % 2) };
  const k = max / short;
  return width <= height
    ? { width: max - (max % 2), height: Math.round((height * k) / 2) * 2 }
    : { width: Math.round((width * k) / 2) * 2, height: max - (max % 2) };
}

/**
 * What an export of a sequence in a preset is (the export dialog's label, 1.0.0-rc.11): the frame, the scan
 * ("1080i50, top field first" or "25p"), and how the sequence sits in a fixed frame of another shape (pillarboxed
 * when narrower, letterboxed when wider), with the note the dialog shows then.
 */
export function exportFrameInfo(preset: ExportPreset, sequence: { width: number; height: number; frameRate: Rational }): {
  width: number; height: number; scan: string; label: string; bars: 'pillarbox' | 'letterbox' | null; note: string | null;
} {
  const out = exportOutputSize(preset, sequence);
  const p = EXPORT_PRESETS[preset];
  const i = p.interlaced ? interlacedName(sequence.frameRate) : null;
  const scan = i ? `${i}, top field first` : `${frameRateLabel(sequence.frameRate)}p`;
  const bars = p.frame && !sameAspect(sequence, out) ? (sequence.width / sequence.height < out.width / out.height ? 'pillarbox' : 'letterbox') : null;
  const shape = aspectLabel(sequence);
  const note = bars ? `This ${shape} sequence is ${bars === 'pillarbox' ? 'pillarboxed' : 'letterboxed'} in ${out.width}×${out.height}.`
    : p.frame && (sequence.width !== out.width || sequence.height !== out.height) ? `This ${sequence.width}×${sequence.height} sequence is scaled to ${out.width}×${out.height}.` : null;
  return { width: out.width, height: out.height, scan, label: `${out.width}×${out.height} · ${scan}`, bars, note };
}

/* ---------------------------------------------------------------- 1.7.0: one export request, several formats */

/** Export error codes of multi-format exports (422). */
export const PRESET_NOT_FOR_FORMAT = 'PRESET_NOT_FOR_FORMAT';
export const FORMAT_UNKNOWN = 'FORMAT_UNKNOWN';
export const EXPORT_FORMAT_ERRORS = { PRESET_NOT_FOR_FORMAT, FORMAT_UNKNOWN } as const;

/**
 * Whether a preset can render a format of this size (1.7.0): broadcast presets have a fixed 1920×1080 frame, so they
 * take 16:9 formats only; web presets take any shape.
 */
export function presetAllowedForFormat(preset: ExportPreset, width: number, height: number): boolean {
  const p = EXPORT_PRESETS[preset];
  if (p.group !== 'broadcast' || !p.frame) return true;
  return sameAspect({ width, height }, p.frame);
}

/** The most formats one sequence export request renders (the primary and MAX_FORMATS others). */
export const MAX_EXPORT_FORMATS = 5;

/**
 * POST /api/v1/sequences/:id/exports `formats` (1.7.0): which formats to render, each with its preset, one row per
 * entry sharing one snapshot. Left out: the primary only, with the request's `preset`.
 */
export const SequenceExportFormatsSchema = z.array(z.object({
  formatId: z.string().min(1).max(64),
  preset: z.enum(EXPORT_PRESET_IDS),
})).min(1).max(MAX_EXPORT_FORMATS).superRefine((list, c) => {
  list.forEach((f, i) => { if (list.findIndex((g) => g.formatId === f.formatId) !== i) c.addIssue({ code: 'custom', path: [i, 'formatId'], message: 'Each format once' }); });
});
export type SequenceExportFormats = z.infer<typeof SequenceExportFormatsSchema>;

/** The response of a sequence export (1.7.0): the first row's ExportDTO (its id is the former exportId) and every row's id. */
export type SequenceExportCreated = ExportDTO & { exportIds: string[] };

/**
 * What a compose worker of this version can render (1.7.0): a job lists what it needs in `requires`, and a worker
 * refuses (fails the job, not retryable) one that needs anything it does not know (missingComposeFeatures). Workers
 * before 1.7.0 ignore the field.
 */
export const COMPOSE_FEATURES = ['framing-keys', 'blur-fit', 'layer-motion', 'audio-tracks', 'graphic-tracks', 'detached-sound', 'volume-keys', 'audio-crossfade', 'track-gain', 'audio-crossfade-align'] as const;
export type ComposeFeature = (typeof COMPOSE_FEATURES)[number];
export const missingComposeFeatures = (requires: readonly string[] | undefined): string[] => (requires ?? []).filter((r) => !(COMPOSE_FEATURES as readonly string[]).includes(r));

/**
 * The compose features a (resolved, one-format) document needs: keys or a zoom, Blur, layer motion; 1.7.3 clips on
 * A3–A8 ('audio-tracks'), a graphic on GFX 2 or 3 ('graphic-tracks'; empty tracks alone do not count) and a storyline
 * clip with soundDetached ('detached-sound'). 1.7.4: a clip with volume keys ('volume-keys'), a crossfade that applies
 * (audio-levels.ts appliedCrossfade > 0; a dormant crossfadeInTicks does not count: 'audio-crossfade') and a sound
 * track with a gain ('track-gain'). 1.8.0: an applied crossfade with crossfadeAlign ('audio-crossfade-align'). Each only when used, so a document an older worker renders the same way does
 * not need a newer one.
 */
export function composeRequirements(doc: z.infer<typeof SequenceDocumentSchema>): ComposeFeature[] {
  const pictures = [...doc.video, ...(doc.layers ?? []).flatMap((l) => l.clips)];
  const out: ComposeFeature[] = [];
  if (pictures.some((c) => c.keys?.length || (c.zoom !== undefined && c.zoom !== 1))) out.push('framing-keys');
  if (doc.video.some((c) => c.fit === 'blur')) out.push('blur-fit');
  if ((doc.layers ?? []).some((l) => l.clips.some((c) => c.motion?.keys.length))) out.push('layer-motion');
  if ((doc.audioTracks ?? []).some((t) => t.clips.length)) out.push('audio-tracks');
  if (doc.graphics.some((g) => g.track)) out.push('graphic-tracks');
  if (doc.video.some((c) => c.soundDetached)) out.push('detached-sound');
  if (allClips(doc).some((c) => c.clip.volumeKeys?.length)) out.push('volume-keys');
  if (doc.video.some((c) => c.crossfadeInTicks && appliedCrossfade(doc, { kind: 'video', id: c.id }) > 0)
    || allAudioTracks(doc).some((t) => t.clips.some((c) => c.crossfadeInTicks && appliedCrossfade(doc, { kind: 'audio', id: c.id }) > 0))) out.push('audio-crossfade');
  if (Object.values(doc.trackAudio ?? {}).some((t) => t.gainDb)) out.push('track-gain');
  // 1.8.0: a crossfade centred on its cut or starting at it, so an older worker refuses rather than ending it at the cut.
  if (doc.video.some((c) => c.crossfadeAlign && appliedCrossfade(doc, { kind: 'video', id: c.id }) > 0)
    || allAudioTracks(doc).some((t) => t.clips.some((c) => c.crossfadeAlign && appliedCrossfade(doc, { kind: 'audio', id: c.id }) > 0))) out.push('audio-crossfade-align');
  return out;
}

export const ComposeJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  /** 1.7.0: the features the sequence needs (COMPOSE_FEATURES); left out when none. */
  requires: z.array(z.string().min(1).max(40)).max(20).optional(),
  correlationId: z.string(),
  exportId: z.string().uuid(),
  preset: z.enum(EXPORT_PRESET_IDS),
  sequence: SequenceDocumentSchema,
  /** Media for every clip id in the sequence (storyline and A2). */
  sources: z.record(z.string(), z.object({ ref: ObjectRefSchema, probe: ProbeResultSchema, quality: z.enum(['original', 'proxy']) })),
  /** Pre-rendered transparent overlays in sequence time. */
  overlays: z.array(z.object({
    kind: z.enum(['graphic', 'subtitle']),
    ref: ObjectRefSchema,
    startTicks: z.number().int().nonnegative(),
    endTicks: z.number().int().positive(),
    /** Graphics are video with alpha; burned subtitles are still images. */
    still: z.boolean(),
  })),
  destination: ObjectRefSchema,
  locations: LocationSpecsSchema,
});
export type ComposeJob = z.infer<typeof ComposeJobSchema>;

export interface ComposeResult {
  destination: { locationId: string; key: string };
  sizeBytes: number;
  checksumSha256: string;
  durationTicks: number;
  frames: number;
  width: number;
  height: number;
  ffmpeg: string;
}

/* ---------------------------------------------------------------- lineage */

export interface ExportLineage {
  sequence: { id: string; name: string; revision: number; versionId: string; versionLabel: string };
  sources: Array<{
    clipId: string;
    track: 'video' | 'audio' | 'layer';
    assetId: string;
    assetTitle: string;
    versionId: string;
    versionNumber: number;
    representation: 'original' | 'proxy';
    checksumSha256: string | null;
    storage: string;
    sourceInTicks: number;
    sourceOutTicks: number;
  }>;
  /** Each graphic with its pinned version and data (typed values for OGraf, 0.93.0; strings for renderAt). */
  graphics: Array<{ instanceId: string; templateId: string; templateVersion: number; templateChecksumSha256: string; engine?: GraphicEngine; data: Record<string, GraphicValue> }>;
  subtitles: { mode: 'none' | 'burn' | 'sidecar' | 'both'; language: string | null; cues: number };
  render: {
    preset: ExportFormatId; quality: 'original' | 'proxy'; ffmpeg: string | null; renderer: string | null; frameRate: { num: number; den: number }; width: number; height: number;
    /** Original format (0.99.0): copied byte for byte, or rewrapped by stream copy; exact when the cut is the range asked for. */
    mode?: 'copy' | 'rewrap'; exact?: boolean;
  };
}

/** The range of an Original format export of part of an asset (0.99.0): asked for, and what the file holds once done. */
export interface ExportRewrapDTO {
  /** The cut is the range asked for (intra-only originals, audio); false when it widened to keyframes. */
  exact: boolean | null;
  inTicks: number;
  outTicks: number;
  actualInTicks: number | null;
  actualOutTicks: number | null;
  /** The muxer it is written with (mov, mp4, mxf, wav…). */
  container: string;
}

export interface ExportDTO {
  id: string;
  sequenceId: string;
  sequenceName: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  preset: ExportFormatId;
  /** What the file is (0.99.0): the preset's label and extension, or the original's format. */
  format: { label: string; extension: string };
  /** An Original format export of a range: the range asked for and the one in the file (0.99.0). */
  rewrap?: ExportRewrapDTO | null;
  quality: 'original' | 'proxy';
  /** 1.2.0: the master read when it was not the primary ("Textless · UHD 2160p25 · ProRes 422 HQ"); null or absent: the primary. */
  master?: { id: string; label: string } | null;
  /** 1.2.0: the batch it was started in (POST /api/v1/exports/batch); null or absent for single and flow exports. */
  batchId?: string | null;
  /** 1.7.0: the sequence format it renders (a format id; null or absent: the primary, as every export before 1.7.0). */
  formatId?: string | null;
  /** 1.7.0: the label of the format it renders ("9:16", "16:9" for the primary of a multi-format request); null or absent before 1.7.0. */
  formatLabel?: string | null;
  /** 1.7.0: the sequence snapshot it renders (null for asset exports); the rows of one multi-format request share it. */
  sequenceVersionId?: string | null;
  subtitleMode: 'none' | 'burn' | 'sidecar' | 'both';
  requestedBy: string | null;
  createdAt: string;
  finishedAt: string | null;
  error: { code: string; message: string; retryable: boolean; hint?: string } | null;
  step: string | null;
  percent: number | null;
  /** Queued (0.95.0): its next step waits for a free worker (since waitingSince), so it is not really running yet. */
  waiting: boolean;
  waitingSince: string | null;
  output: { url: string | null; sidecars: Array<{ format: SidecarFormat; url: string }>; sizeBytes: number | null; durationTicks: number | null; storage: string | null } | null;
  /** Kept in PRISM for downloading until expiresAt (null: kept); expired once removed. */
  download: { expiresAt: string | null; expired: boolean } | null;
  /** Where the export goes: its export location, and for a folder on a storage agent how the delivery went. */
  destination: { id: string; name: string; delivery: 'pending' | 'delivered' | 'failed' | null; error: string | null } | null;
  lineage: ExportLineage | null;
  /**
   * 1.8.0: the rights acknowledged when it was started (exports.rights): warnings confirmed, or an override with its
   * reason and who gave it, and the problems they covered. Absent or null when nothing was asked.
   */
  rights?: ExportRightsDTO | null;
  /**
   * Saved as a new asset, or into a placeholder (0.91.0): the asset once registered, or why it could not be.
   * `failed` means it will not be (the placeholder got media or was deleted); the render is then downloadable.
   */
  asAsset: {
    assetId: string | null; error: string | null;
    placeholder?: { id: string; assetCode: string | null; title: string | null } | null;
    failed?: boolean;
    /** 1.7.0: a further format of a multi-format request saved as an asset waits for the main format's asset (error is null meanwhile). */
    waiting?: boolean;
  } | null;
}

/** 1.8.0: what was acknowledged when an export (or a share link, a hand-off) was started; stored with it. */
export interface ExportRightsDTO {
  confirmed: boolean;
  override: { reason: string; by: string | null; byName: string | null } | null;
  /** The rights problems that were confirmed or overridden. */
  problems: Array<{ code: string; assetId?: string; title?: string; message: string }>;
  at: string;
}

/** 1.7.0: what a further format's export saved as an asset shows until the main format's asset is in. */
export const AS_ASSET_WAITING_FOR_MAIN = 'Waiting for the main format';
